// Listing modes for Xero integration — Dashboard / Connection / Companies & Contacts / Account Mapping.
//
// Modes are ROUTES (PATTERNS.md §"Module weight"), not `useState`. Mirrored
// from src/blank/BlankLayout.tsx (Rule 0 — the canonical reference).
//
//   <base>/dashboard  → Every card on one page (was <base>/overview until H94 —
//                       Aaron 2026-09-28: no "overviews" at a module level)
//   <base>/connection → Unified connection card (OAuth connection + App Credentials)
//   <base>/sync       → Companies sync card
//   <base>/batches    → RETIRED (H255): export batches are their own module,
//                       /app/batchexports (Finance → Accounts Exports).
//                       XeroSettings redirects a saved link there.
//   <base>/mapping    → Account mapping table
//   <base>/config     → Legacy alias for <base>/connection
//   <base>/overview   → Legacy alias for <base>/dashboard

import { migrateModeSegment } from "../components/renamedModeSegments";

import type { ModeDescriptions } from "../components/BreadcrumbDropdownMenu";

export const MODE_LABELS: { [segment: string]: string } = {
  dashboard: "Dashboard",
  connection: "Connection",
  sync: "Companies & Contacts",
  mapping: "Account Mapping",
};

/**
 * Mode segment -> the grey line under its label in the breadcrumb dropdown
 * (BP-85, H76: "ALL model switchers should have name and description the same
 * as demand"). Same keys as the label map; each read off what the mode's
 * component renders, not paraphrased from the label.
 */
export const MODE_DESCRIPTIONS: ModeDescriptions = {
  dashboard: "Connection, sync and mapping",
  connection: "Your own Xero login",
  sync: "Company sync and import",
  mapping: "Accounts and tax types",
};

export const DEFAULT_MODE_SEGMENT = "dashboard";

/**
 * The retired Export Batches mode (H255). Not a mode: a remembered or saved
 * `batches` never renders here, the page sends it to the Accounts Exports
 * module instead (router-relative, basename `/app`).
 */
export const RETIRED_BATCHES_SEGMENT = "batches";
export const BATCH_EXPORTS_PATH = "/batchexports";

export const MODE_STORAGE_KEY = "xero.viewMode";

/**
 * Is this a real mode segment?
 *
 * hasOwnProperty, NOT `in` — `in` walks the prototype chain, so "constructor",
 * "toString", "valueOf" and "__proto__" all test true against an object literal
 * and would pass as valid modes.
 *
 * Accepts legacy "config" as a recognized mode segment that maps to "connection",
 * and legacy "overview" (renamed "dashboard", H94) the same way.
 */
export const isModeSegment = (segment: string): boolean =>
  segment === "config" ||
  segment === "overview" ||
  Object.prototype.hasOwnProperty.call(MODE_LABELS, segment);

/**
 * Redirect target for a bare visit to the module root. Reads the last mode
 * this user visited, falling back to DEFAULT_MODE_SEGMENT when unset or when
 * the stored value is no longer a valid mode.
 */
export function readRememberedModeSegment(): string {
  if (typeof window === "undefined") return DEFAULT_MODE_SEGMENT;
  const stored = migrateModeSegment(window.localStorage.getItem(MODE_STORAGE_KEY));
  if (stored === "config") return "connection";
  return stored && isModeSegment(stored) ? stored : DEFAULT_MODE_SEGMENT;
}

/**
 * Split a pathname into its (nav-driven) base path and the trailing mode segment.
 *
 * On "/app/xero/mapping" gives:
 *   basePath:    "/app/xero"
 *   currentMode: "mapping"
 *   onMode:      true
 *
 * On bare "/app/xero":
 *   basePath:    "/app/xero"
 *   currentMode: readRememberedModeSegment() (defaults to "connection")
 *   onMode:      false
 */
export function splitModePath(pathname: string): {
  basePath: string;
  currentMode: string;
  onMode: boolean;
} {
  const parts = pathname.split("/").filter(Boolean);
  const modeCandidate = parts[parts.length - 1] || "";
  const onMode = isModeSegment(modeCandidate);
  const rawMode = onMode ? modeCandidate : readRememberedModeSegment();
  const currentMode =
    rawMode === "config" ? "connection" : migrateModeSegment(rawMode);

  const xeroIndex = parts.indexOf("xero");
  const basePath =
    xeroIndex !== -1
      ? "/" + parts.slice(0, xeroIndex + 1).join("/")
      : onMode
      ? "/" + parts.slice(0, -1).join("/")
      : "/" + parts.join("/");

  return { basePath, currentMode, onMode };
}
